import styled, { css } from 'styled-components'
import { ifProp } from 'styled-tools'
import {
  buttonSizeOptions,
  buttonTypeOptions,
  buttonLinkStyleOptions,
  buttonLinkSizeOptions,
  buttonSizeStyleOptions,
  buttonSideOptions,
  buttonSideOptionsStyle
} from './constants'
import colorsOptions, {
  applyOpacity
} from '../../fundamentals/colors-styles/constants'
import {
  textStyleWeightOptions,
  textStyleWeightStyleOptions
} from '../../fundamentals/text-styles/constants'
import { motion } from 'framer-motion'

const ButtonBase = styled(motion.button)`
  all: unset;
  box-sizing: border-box;
  width: ${ifProp('fullWidth', '100%')};
  border: none;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  border-radius: 84px;
  ${textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-600']]}

  ${({ size = buttonSizeOptions.MEDIUM }) => css`
    ${buttonSizeStyleOptions[size]}
  `}

  ${({ disableIconStyle = false }) =>
    !disableIconStyle &&
    css`
      & svg {
        ${({ size = buttonSizeOptions.MEDIUM }) => css`
          ${buttonSizeStyleOptions[size].iconSize}
          stroke: inherit;
        `}
        & path {
          stroke: inherit;
          fill: inherit;
        }
      }
    `}

  ${({ buttonType }) =>
    buttonType === buttonTypeOptions['ICON-ONLY'] &&
    css`
      padding: 0;
      border-radius: 100%;
      display: flex;
      align-items: center;
      justify-content: center;

      ${({ size = buttonSizeOptions.MEDIUM }) => css`
        width: ${buttonSizeStyleOptions[size]?.height};
      `}
    `}

  &:hover {
    cursor: pointer;
  }

  &:focus-visible {
    outline-style: solid;
    outline-width: 2px;
    outline-color: ${colorsOptions.docuToolsOrange.orange};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: '50%';
  }

  padding: ${ifProp('fullWidth', 0)};
`

const PrimaryButton = styled(ButtonBase)`
  background-color: ${colorsOptions.docuToolsOrange.orange};
  color: ${colorsOptions.greys.white};
  stroke: ${colorsOptions.greys.white};
  fill: ${colorsOptions.greys.white};
  &:hover {
    background-color: ${colorsOptions.docuToolsOrange['dt-40']};
  }
  &:active {
    background-color: ${colorsOptions.docuToolsOrange['dt-50']};
  }
  &:disabled {
    background-color: ${colorsOptions.greys['grey-40']};
    color: ${colorsOptions.greys['grey-10']};
  }
`

const PrimaryDestructiveButton = styled(PrimaryButton)`
  background-color: ${colorsOptions.semantics.danger.error};
  &:hover {
    background-color: ${colorsOptions.semantics.danger['error-50']};
  }
  &:active {
    background-color: ${colorsOptions.semantics.danger['error-60']};
  }
`

const SecondaryHollowButton = styled(ButtonBase)`
  background-color: transparent;
  color: ${colorsOptions.greys['grey-90']};
  stroke: ${colorsOptions.greys['grey-90']};
  fill: ${colorsOptions.greys['grey-90']};
  border-style: solid;
  border-color: ${colorsOptions.greys['grey-90']};

  &:hover {
    background-color: ${colorsOptions.greys['grey-20']};
  }
  &:active {
    background-color: ${colorsOptions.greys['grey-30']};
  }

  &:disabled {
    background-color: transparent;
    color: ${colorsOptions.greys['grey-40']};
    stroke: ${colorsOptions.greys['grey-40']};
    fill: ${colorsOptions.greys['grey-40']};
    border-color: ${colorsOptions.greys['grey-40']};
  }
`
const SecondaryWhiteFillButton = styled(SecondaryHollowButton)`
  background-color: ${colorsOptions.greys.white};
`

const SecondaryInvertedButton = styled(ButtonBase)`
  background-color: transparent;
  color: ${colorsOptions.greys.white};
  stroke: ${colorsOptions.greys.white};
  fill: ${colorsOptions.greys.white};
  border-style: solid;
  border-color: ${colorsOptions.greys.white};

  &:hover {
    background-color: ${colorsOptions.greys['grey-80']};
  }
  &:active {
    background-color: ${colorsOptions.greys['grey-70']};
  }
  &:disabled {
    background-color: transparent;
    color: ${colorsOptions.greys['grey-60']};
    stroke: ${colorsOptions.greys['grey-60']};
    fill: ${colorsOptions.greys['grey-60']};
    border-color: ${colorsOptions.greys['grey-60']};
  }
`

const SecondaryDestructiveHollowButton = styled(ButtonBase)`
  background-color: transparent;
  color: ${colorsOptions.semantics.danger['error-60']};
  stroke: ${colorsOptions.semantics.danger['error-60']};
  fill: ${colorsOptions.semantics.danger['error-60']};
  border-style: solid;
  border-color: ${colorsOptions.semantics.danger['error-60']};

  &:hover {
    background-color: ${colorsOptions.semantics.danger['error-10']};
  }
  &:active {
    background-color: ${colorsOptions.semantics.danger['error-20']};
  }
  &:disabled {
    background-color: transparent;
    color: ${colorsOptions.greys['grey-40']};
    stroke: ${colorsOptions.greys['grey-40']};
    fill: ${colorsOptions.greys['grey-40']};
    border-color: ${colorsOptions.greys['grey-40']};
  }
`

const SecondaryDestructiveWhiteFillButton = styled(
  SecondaryDestructiveHollowButton
)`
  background-color: ${colorsOptions.greys.white};
`

const SecondaryDestructiveInvertedButton = styled(ButtonBase)`
  background-color: transparent;
  color: ${colorsOptions.semantics.danger.error};
  stroke: ${colorsOptions.semantics.danger.error};
  fill: ${colorsOptions.semantics.danger.error};
  border-style: solid;
  border-color: ${colorsOptions.semantics.danger.error};

  &:hover {
    background-color: ${colorsOptions.greys['grey-80']};
  }
  &:active {
    background-color: ${colorsOptions.greys['grey-70']};
  }
  &:disabled {
    background-color: transparent;
    color: ${colorsOptions.greys['grey-60']};
    stroke: ${colorsOptions.greys['grey-60']};
    fill: ${colorsOptions.greys['grey-60']};
    border-color: ${colorsOptions.greys['grey-60']};
  }
`

const TertiaryHollowButton = styled(ButtonBase)`
  background-color: transparent;
  color: ${colorsOptions.greys['grey-90']};
  stroke: ${colorsOptions.greys['grey-90']};
  fill: ${colorsOptions.greys['grey-90']};
  border-style: solid;
  border-color: ${colorsOptions.greys['grey-20']};

  &:hover {
    background-color: ${colorsOptions.greys['grey-20']};
  }
  &:active {
    background-color: ${colorsOptions.greys['grey-30']};
  }
  &:disabled {
    background-color: transparent;
    color: ${colorsOptions.greys['grey-40']};
    stroke: ${colorsOptions.greys['grey-40']};
    fill: ${colorsOptions.greys['grey-40']};
    border-color: ${colorsOptions.greys['grey-40']};
  }
`

const TertiaryWhiteFillButton = styled(TertiaryHollowButton)`
  background-color: ${colorsOptions.greys.white};
`

const GhostButton = styled(ButtonBase)`
  background-color: transparent;
  color: ${colorsOptions.greys['grey-90']};
  stroke: ${colorsOptions.greys['grey-90']};
  fill: ${colorsOptions.greys['grey-90']};
  border: none;

  &:hover {
    background-color: ${colorsOptions.greys['grey-20']};
  }
  &:active {
    background-color: ${colorsOptions.greys['grey-30']};
  }
  &:disabled {
    background-color: transparent;
    color: ${colorsOptions.greys['grey-40']};
    stroke: ${colorsOptions.greys['grey-40']};
    fill: ${colorsOptions.greys['grey-40']};
  }
`

const GhostInvertedButton = styled(ButtonBase)`
  background-color: transparent;
  color: ${colorsOptions.greys.white};
  stroke: ${colorsOptions.greys.white};
  fill: ${colorsOptions.greys.white};
  border: none;

  &:hover {
    background-color: ${colorsOptions.greys['grey-80']};
  }
  &:active {
    background-color: ${colorsOptions.greys['grey-70']};
  }
  &:disabled {
    background-color: transparent;
    color: ${colorsOptions.greys['grey-60']};
    stroke: ${colorsOptions.greys['grey-60']};
    fill: ${colorsOptions.greys['grey-60']};
  }
`
const GhostDestructiveButton = styled(GhostButton)`
  color: ${colorsOptions.semantics.danger['error-60']};
  stroke: ${colorsOptions.semantics.danger['error-60']};
  fill: ${colorsOptions.semantics.danger['error-60']};

  &:hover {
    background-color: ${colorsOptions.semantics.danger['error-10']};
  }
  &:active {
    background-color: ${colorsOptions.semantics.danger['error-20']};
  }
`

const GhostDestructiveInvertedButton = styled(GhostInvertedButton)`
  color: ${colorsOptions.semantics.danger.error};
  stroke: ${colorsOptions.semantics.danger.error};
  fill: ${colorsOptions.semantics.danger.error};
`

const LinkButton = styled(ButtonBase)`
  all: unset;
  ${textStyleWeightStyleOptions[textStyleWeightOptions['TEXT-500']]}
  color: ${colorsOptions.greys['grey-70']};

  ${({ emphasis }) =>
    emphasis &&
    css`
      color: ${colorsOptions.docuToolsOrange.orange};
    `}

  ${({ linkSize = buttonLinkSizeOptions.MEDIUM }) => css`
    ${buttonLinkStyleOptions[linkSize]};
  `}

  &:active {
    color: ${colorsOptions.greys['grey-80']};

    ${({ emphasis }) =>
      emphasis &&
      css`
        color: ${colorsOptions.docuToolsOrange['dt-40']};
      `}
  }

  &:hover,
  &:focus {
    text-decoration: underline;
  }

  &:disabled {
    color: ${colorsOptions.greys['grey-50']};
  }
`

const ImageOverlayButton = styled.button`
  box-sizing: border-box;
  width: ${ifProp('fullWidth', '100%', 'fit-content')};
  white-space: nowrap;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  overflow: visible;
  background: transparent;
  border-radius: 150px;
  color: ${colorsOptions.greys.white};
  gap: 0.5rem;

  ${({ size = buttonSizeOptions.MEDIUM }) => css`
    ${buttonSizeStyleOptions[size]}
  `}

  ${({ disableIconStyle = false }) =>
    !disableIconStyle &&
    css`
      & svg {
        ${({ size = buttonSizeOptions.MEDIUM }) => css`
          ${buttonSizeStyleOptions[size].iconSize}
        `}
      }
    `}

  padding: 0.5rem 1.5rem;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-color: ${applyOpacity(colorsOptions.greys['grey-100'], 0.5)};
    border-radius: ${ifProp('fullWidth', '0', '150')}px;
    z-index: 0;

    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  &:hover::before {
    background-color: ${applyOpacity(colorsOptions.greys['grey-100'], 0.6)};
  }

  &:active::before {
    background-color: ${applyOpacity(colorsOptions.greys['grey-100'], 0.67)};
  }

  &:focus-visible::after {
    content: '';
    position: absolute;
    top: -3px;
    left: -3px;
    right: -3px;
    bottom: -3px;
    border: 2px solid ${colorsOptions.docuToolsOrange.orange};
    border-radius: ${ifProp('fullWidth', '0', '150')}px;

    z-index: 0;
    transition: border-color 0.2s;
  }

  &:disabled {
    cursor: not-allowed;
    ${({ disableIconStyle = false }) =>
      !disableIconStyle &&
      css`
        & svg {
          & path {
            fill: ${colorsOptions.greys['grey-40']};
            stroke: ${colorsOptions.greys['grey-40']};
            color: ${colorsOptions.greys['grey-40']};
          }
        }
      `}
  }

  &:disabled::before {
    background-color: ${applyOpacity(colorsOptions.greys['grey-40'], 0.1)};
  }

  ${({ backgroundColor }) =>
    backgroundColor &&
    css`
      &::before,
      &:hover::before,
      &:active::before {
        background-color: ${backgroundColor};
        backdrop-filter: none;
        -webkit-backdrop-filter: none;
      }
    `}

  ${({ buttonType }) =>
    buttonType === buttonTypeOptions['ICON-ONLY'] &&
    css`
      padding: 0.5rem;
      border-radius: 50%;
      width: fit-content;

      &::before {
        border-radius: 50%;
      }

      &::after {
        border-radius: 50%;
      }
    `}
`

const FloatingButton = styled(ImageOverlayButton)`
  &::before {
    background-color: ${applyOpacity(colorsOptions.greys.white, 0.8)};
    box-shadow:
      0 1px 2px 0 ${applyOpacity(colorsOptions.greys['grey-100'], 0.32)},
      0 1px 3px 0 ${applyOpacity(colorsOptions.greys['grey-100'], 0.16)};
  }

  &:hover::before {
    background-color: ${applyOpacity(colorsOptions.greys['grey-10'], 0.8)};
  }

  &:active::before {
    background-color: ${applyOpacity(colorsOptions.greys['grey-20'], 0.8)};
    box-shadow: none;
  }

  &:disabled::before {
    background-color: ${applyOpacity(colorsOptions.greys['grey-10'], 0.8)};
  }
`

const SideImageOverlay = styled(ImageOverlayButton)`
  ${ifProp(
    { side: 'left' },
    `border-right: 2px solid ${colorsOptions.greys.white};`
  )}

  ${ifProp(
    { side: 'right' },
    `border-left: 2px solid ${colorsOptions.greys.white};`
  )}
  
  ${({ side = buttonSideOptions.RIGHT }) => css`
    padding: ${buttonSideOptionsStyle[side].padding};
    border-radius: ${buttonSideOptionsStyle[side].borderRadius};
  `}

  &::before {
    ${({ side = buttonSideOptions.RIGHT }) => css`
      border-radius: ${buttonSideOptionsStyle[side].borderRadius};
    `}
  }

  &:focus::after {
    border: none;
  }
`

export {
  PrimaryButton,
  PrimaryDestructiveButton,
  SecondaryHollowButton,
  SecondaryWhiteFillButton,
  SecondaryInvertedButton,
  SecondaryDestructiveHollowButton,
  SecondaryDestructiveWhiteFillButton,
  SecondaryDestructiveInvertedButton,
  TertiaryHollowButton,
  TertiaryWhiteFillButton,
  GhostButton,
  GhostInvertedButton,
  GhostDestructiveButton,
  GhostDestructiveInvertedButton,
  LinkButton,
  ImageOverlayButton,
  FloatingButton,
  SideImageOverlay
}
